import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { IndustryOverviewTitle } from "app/pages/industry-analysis/overview/components/common/IndustryOverviewTitle/IndustryOverviewTitle";
import type { IServices } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/IndustryOverviewContext";
import DurationService, { BasicDurations } from "app/services/DurationService";

const { orange, blue } = colorsPalettes;

interface IHeader {
    isLoading: boolean;
    title: string;
    showVsDate?: boolean;
    showFrom?: boolean;
    tooltip?: string;
    displayBetaLabel?: boolean;
    showLatest?: boolean;
    services?: IServices;
    isPop?: boolean;

    addToDashboardProps?: {
        isVisible: boolean;
        type?: IWidgetModelTypesType;
        metric?: string;
        overrideParams?: Record<string, string | number>;
    };
}

const Header = (props: IHeader) => {
    const {
        isLoading = true,
        title,
        showVsDate = false,
        tooltip,
        displayBetaLabel = false,
        showFrom = true,
        showLatest,
        services,
        isPop = false,
        addToDashboardProps = {
            isVisible: false,
        },
    } = props;
    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration } = params;
    const { from, to, compareFrom, compareTo } = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration).forAPI,
        [duration, comparedDuration],
    );
    const latestTo = DurationService.getDurationData(BasicDurations.LAST_MONTH).forAPI.to;

    const headerConfig = useMemo(() => {
        const titleKey = translate(title);

        const getHeaderFilters = () => {
            if (isPop && compareFrom && compareTo) {
                return [
                    {
                        filter: "date",
                        value: {
                            from,
                            to,
                            compareFrom,
                            compareTo,
                            coloredBullets: {
                                dateBullet: blue[400],
                                compareDateBullet: orange[400],
                            },
                        },
                    },
                ];
            }
            if (showVsDate) {
                return [
                    {
                        filter: "date",
                        value: {
                            from,
                            to,
                            dateSeparator: translate("global.vs"),
                            format: "MMMM YYYY",
                        },
                    },
                ];
            }
            if (showFrom) {
                return [
                    {
                        filter: "date",
                        value: {
                            from: from,
                            to: to,
                            format: "MMMM YYYY",
                        },
                    },
                ];
            }
            return [
                {
                    filter: "date",
                    value: {
                        to: showLatest ? latestTo : to,
                        format: "MMMM YYYY",
                    },
                },
            ];
        };

        return {
            title: titleKey,
            filters: getHeaderFilters(),
        };
    }, [from, to, compareFrom, compareTo, isPop, showVsDate, showFrom, showLatest, title, latestTo]);

    const headerProps = useMemo(() => {
        return {
            title: headerConfig.title,
            tooltip: tooltip,
            filters: headerConfig.filters,
            displayBetaLabel,
        };
    }, [headerConfig.filters, headerConfig.title, tooltip, displayBetaLabel]);

    return isLoading ? (
        <Flex
            gap="10px"
            minHeight="53px"
            padding="16px 24px 8px 24px"
            flexDirection="column"
            borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
        >
            <PixelPlaceholderLoader
                width={185}
                height={22}
            />
            <PixelPlaceholderLoader
                width={109}
                height={14}
            />
        </Flex>
    ) : (
        <IndustryOverviewTitle
            headerProps={headerProps}
            services={services}
            addToDashboardProps={addToDashboardProps}
        />
    );
};

export default Header;
